import React from 'react';
import { Link } from 'react-router-dom';
import CopyPrompt from '../Common/CopyPrompt';

const WorkdayAssistantPage = () => {
  const hrQueries = [
    "Show me all time off requests for this year and how many are left?",
    "What benefits am I enrolled in?",
    "Show me all my training and learning records.",
    "Can you clock me in right now?",
    "Have I clocked in today?",
    "Who on my team has pending time-off requests?",
    "Which of my team members have overdue training?",
    "Can you name the team member who hasn't submitted their timesheets yet?"
  ];

  const sisQueries = [
    "Can you give me a detailed transcript of my academic record?",
    "What courses am I registered for this semester?",
    "Do I have a pending fee payment?",
    "What are my grades for the 2024 semester?",
    "What is my schedule for this year?",
    "How many courses and unit credits are required for me to graduate?"
  ];

  return (
    <div className="min-h-screen bg-purple-50 pb-20">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-12">
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Workday Assistant Lab
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-gray-700 max-w-4xl mx-auto leading-relaxed px-2">
            The <strong>Workday Assistant</strong> removes friction from everyday Workday tasks. Instead of navigating multiple dashboards, teams can ask questions in natural language and receive secure, real-time responses sourced directly from <strong>Workday</strong>.
          </p>
          <p className="text-base sm:text-lg md:text-xl text-gray-700 max-w-4xl mx-auto leading-relaxed px-2 mt-4">
            In this lab, you'll explore both <strong>Workday HR</strong> and <strong>Workday SIS</strong> assistants to experience how AI simplifies HR and student workflows.
          </p>

          <div className="mt-6 mb-4">
            <a 
              href="https://app.campusmind.ai/agents-market" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-block bg-purple-700 text-white px-6 py-3 rounded-lg font-semibold hover:bg-purple-800 transition-all duration-200 shadow-lg hover:shadow-xl text-base sm:text-lg"
            >
              Try Workday Assistant →
            </a>
          </div>
        </div>

        {/* Workday Assistant Overview Image */}
        <div className="bg-white rounded-xl shadow-lg p-6 md:p-8 mb-8">
          <div className="flex justify-center">
            <img
              src="/workdayassistant.png"
              alt="Workday Assistant Overview"
              className="w-full max-w-4xl rounded-lg shadow-md"
            />
          </div>
        </div>

        {/* HR Lab Section */}
        <div className="bg-white rounded-xl shadow-lg p-6 md:p-8 mb-8 border-l-8 border-blue-500">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-700 rounded-full flex items-center justify-center text-white shadow-lg">
                <span className="text-2xl font-bold">HR</span>
              </div>
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">HR Lab: Workday HR Assistant</h2>
              <p className="text-gray-700 mb-4">In this lab, you'll interact with the <strong>Workday HR Agent</strong> to experience how AI simplifies HR workflows within Workday.</p>
              
              {/* Step 1: Access the Workday HR Agent */}
              <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-blue-900 mb-2">Step 1: Access the Workday HR Agent</h3>
                <p className="text-blue-800 mb-2">Steps:</p>
                <ul className="text-blue-800 space-y-1">
                  <li>• Go to the <strong>Agent Marketplace</strong></li>
                  <li>• Scroll down to the <strong>Human Resources</strong> category</li>
                  <li>• Find the agent named <strong>"Workday HR Agent."</strong></li>
                  <li>• Click to open the chat window</li>
                </ul>
              </div>

              {/* Step 2: Ask HR Questions */}
              <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-blue-900 mb-2">Step 2: Ask HR Questions</h3>
                <p className="text-blue-800 mb-2">Start asking the agent natural language questions like:</p>
                <div className="bg-white border border-blue-200 rounded p-3 mb-3">
                  <p className="text-lg font-mono text-blue-900">"Show me all time off requests for this year and how many are left?"</p>
                </div>
                <p className="text-blue-800 mb-2">✅ The agent will:</p>
                <ul className="text-blue-800 space-y-1">
                  <li>• Retrieve your time-off data directly from Workday</li>
                  <li>• Display your current balance and any pending requests</li>
                </ul>
              </div>

              {/* Step 3: Verify on Workday */}
              <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-blue-900 mb-2">Step 3: Verify on Workday</h3>
                <p className="text-blue-800 mb-2">Open your Workday dashboard to confirm the responses.</p>
                <p className="text-blue-800 mb-2">Use the following link for verification:</p>
                <div className="bg-white border border-blue-200 rounded p-3 mb-3">
                  <a 
                    href="https://impl.wd12.myworkday.com/royalcyber_dpt1/learning" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-700 hover:text-blue-900 underline"
                  >
                    🔗 Workday Portal
                  </a>
                </div>
              </div>

              {/* Additional HR Questions */}
              <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-blue-900 mb-2">Additional HR Questions</h3>
                <p className="text-blue-800 mb-2">Try these additional queries to explore HR capabilities:</p>
                <div className="space-y-2">
                  {hrQueries.slice(1).map((query, index) => (
                    <div key={index} className="flex items-center justify-between gap-3 p-3 bg-white rounded-lg border border-blue-200">
                      <span className="text-gray-700 flex-1">{query}</span>
                      <CopyPrompt text={query} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* SIS Lab Section */}
        <div className="bg-white rounded-xl shadow-lg p-6 md:p-8 mb-8 border-l-8 border-green-500">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-gradient-to-br from-green-600 to-green-700 rounded-full flex items-center justify-center text-white shadow-lg">
                <span className="text-2xl font-bold">SIS</span>
              </div>
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">SIS Lab: Workday Student Information Services Assistant</h2>
              <p className="text-gray-700 mb-4">In this section, you'll use the <strong>Student Services Agent</strong> to access academic records and information.</p>
              
              {/* Step 1: Access the Student Services Agent */}
              <div className="bg-green-50 border-2 border-green-300 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-green-900 mb-2">Step 1: Access the Student Services Agent</h3>
                <p className="text-green-800 mb-2">Steps:</p>
                <ul className="text-green-800 space-y-1">
                  <li>• Go to the <strong>Agent Marketplace</strong></li>
                  <li>• Scroll down to the <strong>Student Affairs</strong> category</li>
                  <li>• Find the agent named <strong>"Student Services Agent."</strong></li>
                  <li>• Click to open the chat window</li>
                </ul>
              </div>

              {/* Step 2: Ask SIS Questions */}
              <div className="bg-green-50 border-2 border-green-300 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-green-900 mb-2">Step 2: Ask SIS Questions</h3>
                <p className="text-green-800 mb-2">Ask the agent:</p>
                <div className="bg-white border border-green-200 rounded p-3 mb-3">
                  <p className="text-lg font-mono text-green-900">"Can you give me a detailed transcript of my academic record?"</p>
                </div>
                <p className="text-green-800 mb-2">✅ The agent will:</p>
                <ul className="text-green-800 space-y-1">
                  <li>• Retrieve your official transcript from the Workday SIS system</li>
                  <li>• Display details such as GPA, completed courses, and grades</li>
                </ul>
              </div>

              {/* Step 3: Verify on Workday */}
              <div className="bg-green-50 border-2 border-green-300 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-green-900 mb-2">Step 3: Verify on Workday</h3>
                <p className="text-green-800 mb-2">Check the accuracy of the agent's response by logging in to your Workday Student portal:</p>
                <div className="bg-white border border-green-200 rounded p-3 mb-3">
                  <a 
                    href="https://sales100.wd99.myworkday.net/na_royalcyberamu0101v2025r1/d/inst/15$374228/14832$72.htmld#TABTASKID=2998%2427738" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-green-700 hover:text-green-900 underline"
                  >
                    🔗 Workday SIS Portal
                  </a>
                </div>
              </div>

              {/* Additional SIS Questions */}
              <div className="bg-green-50 border-2 border-green-300 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-green-900 mb-2">Additional SIS Questions</h3>
                <p className="text-green-800 mb-2">Explore the SIS agent further by asking:</p>
                <div className="space-y-2">
                  {sisQueries.slice(1).map((query, index) => (
                    <div key={index} className="flex items-center justify-between gap-3 p-3 bg-white rounded-lg border border-green-200">
                      <span className="text-gray-700 flex-1">{query}</span>
                      <CopyPrompt text={query} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Wrap-Up */}
        <div className="bg-gradient-to-r from-blue-600 to-green-600 rounded-xl shadow-xl p-8 text-white mb-8">
          <h2 className="text-2xl font-bold mb-6 text-center">Wrap-Up</h2>
          <div className="space-y-4">
            <p className="text-lg text-white">🎓 Congratulations! You've now explored both <strong>Workday HR</strong> and <strong>Workday SIS Assistants</strong>.</p>
            <p className="text-lg text-white">You've learned how AI-powered agents can:</p>
            <ul className="space-y-2 text-white">
              <li className="flex items-start gap-2">
                <span className="text-green-200">✓</span>
                <span>Retrieve real-time data from Workday securely</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-200">✓</span>
                <span>Simplify employee and student workflows</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-200">✓</span>
                <span>Reduce navigation time by providing instant, conversational access to HR and academic information</span>
              </li>
            </ul>
            <p className="text-lg font-bold text-white mt-4">
              🚀 The Workday Assistant represents a major leap in user productivity and data accessibility within enterprise systems.
            </p>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-center gap-4 flex-wrap">
          <Link
            to="/it-helpdesk-agent"
            className="bg-gray-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-gray-700 transition-colors duration-200"
          >
            Back: IT Workforce
          </Link>
          <Link
            to="/recruiting-workforce"
            className="bg-purple-700 text-white px-6 py-3 rounded-lg font-medium hover:bg-purple-800 transition-colors duration-200"
          >
            Next: Recruiting Workforce
          </Link>
        </div>
      </div>
    </div>
  );
};

export default WorkdayAssistantPage;
